import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  assignOpsCase,
  cancelOpsCase,
  completeOpsCase,
  enqueueOpsCase,
  fetchOpsAssignees,
  fetchOpsCaseSummary,
  holdOpsCaseItem,
  issueOpsCaseItem,
  listOpsCaseItems,
  listOpsCases,
  openOpsCase,
  reopenOpsCase,
  resetOpsCaseItem,
  setOpsCoordinator,
  type OpsCaseListQuery,
  markOpsCaseAlreadyBooked,
} from './ops-cases.api';
import {
  decideOpsApproval,
  deleteOpsCaseDocument,
  fetchFundProof,
  fetchLeadOpsDocumentUrl,
  fetchOpsSellerScreenshotUrl,
  fetchOpsBookingSheet,
  fetchOpsCaseDocumentUrl,
  listLeadOpsDocuments,
  listOpsCaseDocuments,
  listOpsSellerScreenshots,
  uploadOpsCaseDocument,
  fetchOpsPaymentProof,
  listFundRequests,
  listOpsAmendments,
  listOpsApprovals,
  markFundPaid,
  requestOpsAmendment,
  requestOpsFund,
  resolveOpsAmendment,
} from './ops-cases.api';

import type {
  OpsAmendmentInput,
  OpsApprovalDecisionInput,
  OpsDocumentUploadInput,
  OpsHoldItemInput,
  OpsIssueItemInput,
  OpsMarkPaidInput,
  OpsRequestFundInput,
  OpsResolveAmendmentInput,
} from '@utazzo/shared';

import { extractServerError } from '@/lib/api-error';

const msg = (err: unknown): string | null => extractServerError(err).message;

export const opsCasesQK = {
  all: ['ops-cases'] as const,
  list: (q: OpsCaseListQuery) => ['ops-cases', 'list', q] as const,
  summary: (assignedToId?: string) => ['ops-cases', 'summary', assignedToId ?? null] as const,
  assignees: ['ops-cases', 'assignees'] as const,
  items: (id: string) => ['ops-cases', 'items', id] as const,
  approvals: ['ops-cases', 'approvals'] as const,
  fundRequests: (view: string) => ['ops-cases', 'fund-requests', view] as const,
  amendments: (leadId?: string) => ['ops-cases', 'amendments', leadId ?? null] as const,
  documents: (caseId: string) => ['ops-cases', 'documents', caseId] as const,
  sellerScreenshots: (caseId: string) => ['ops-cases', 'seller-screenshots', caseId] as const,
  sheet: (caseId: string) => ['ops-cases', 'sheet', caseId] as const,
  leadDocuments: (leadId: string) => ['ops-cases', 'lead-documents', leadId] as const,
};

/** The queue moves on its own (round-robin cron), so refresh every 30s. */
const LIVE_MS = 30_000;

export const useOpsCasesList = (query: OpsCaseListQuery) =>
  useQuery({
    queryKey: opsCasesQK.list(query),
    queryFn: () => listOpsCases(query),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE_MS,
    refetchOnWindowFocus: true,
  });

export const useOpsCaseSummary = (assignedToId?: string) =>
  useQuery({
    queryKey: opsCasesQK.summary(assignedToId),
    queryFn: () => fetchOpsCaseSummary(assignedToId),
    refetchInterval: LIVE_MS,
    refetchOnWindowFocus: true,
  });

export const useOpsAssignees = (enabled: boolean) =>
  useQuery({
    queryKey: opsCasesQK.assignees,
    queryFn: fetchOpsAssignees,
    enabled,
    staleTime: 60_000,
  });

const useCaseMutation = <TArgs>(
  fn: (args: TArgs) => Promise<unknown>,
  success: string | null,
  failure: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opsCasesQK.all });
      if (success) toast.success(success);
    },
    onError: (err) => toast.error(msg(err) ?? failure),
  });
};

export const useOpenOpsCase = () =>
  useCaseMutation((id: string) => openOpsCase(id), null, 'Could not open the lead');
export const useCompleteOpsCase = () =>
  useCaseMutation((id: string) => completeOpsCase(id), 'Marked as completed', 'Could not complete');
export const useMarkAlreadyBooked = () =>
  useCaseMutation(
    (id: string) => markOpsCaseAlreadyBooked(id),
    'Marked as already booked',
    'Could not mark as already booked',
  );
export const useReopenOpsCase = () =>
  useCaseMutation((id: string) => reopenOpsCase(id), 'Reopened', 'Could not reopen');
export const useCancelOpsCase = () =>
  useCaseMutation(
    (id: string) => cancelOpsCase(id),
    'Removed from the ops queue',
    'Could not cancel',
  );
export const useAssignOpsCase = () =>
  useCaseMutation(
    (args: { id: string; userId: string | null }) => assignOpsCase(args.id, args.userId),
    'Assignment updated',
    'Could not reassign',
  );
export const useSetOpsCoordinator = () =>
  useCaseMutation(
    (args: { id: string; userId: string }) => setOpsCoordinator(args.id, args.userId),
    'Operations owner updated',
    'Could not change the Operations owner',
  );
export const useEnqueueOpsCase = () =>
  useCaseMutation(
    (leadId: string) => enqueueOpsCase(leadId),
    'Sent to operations',
    'Could not send to operations',
  );

export const useOpsBookingSheet = (id: string | null) =>
  useQuery({
    queryKey: opsCasesQK.sheet(id ?? ''),
    queryFn: () => fetchOpsBookingSheet(id as string),
    enabled: Boolean(id),
  });

export const useOpsCaseItems = (id: string | null) =>
  useQuery({
    queryKey: opsCasesQK.items(id ?? ''),
    queryFn: () => listOpsCaseItems(id as string),
    enabled: Boolean(id),
  });

/** Item mutations refresh the item list AND the grid (counts, pay-by). */
const useItemMutation = <TArgs>(
  fn: (args: TArgs) => Promise<unknown>,
  success: string,
  failure: string,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opsCasesQK.all });
      toast.success(success);
    },
    onError: (err) => toast.error(msg(err) ?? failure),
  });
};

export const useHoldOpsCaseItem = (caseId: string) =>
  useItemMutation(
    (args: { componentId: string; input: OpsHoldItemInput }) =>
      holdOpsCaseItem(caseId, args.componentId, args.input),
    'Put on hold',
    'Could not save the hold',
  );

export const useIssueOpsCaseItem = (caseId: string) =>
  useItemMutation(
    (args: { componentId: string; input: OpsIssueItemInput }) =>
      issueOpsCaseItem(caseId, args.componentId, args.input),
    'Marked as issued',
    'Could not mark as issued',
  );

export const useResetOpsCaseItem = (caseId: string) =>
  useItemMutation(
    (componentId: string) => resetOpsCaseItem(caseId, componentId),
    'Moved back to "to book"',
    'Could not reset the item',
  );

// --- Fund flow ---

export const useRequestOpsFund = (caseId: string) =>
  useItemMutation(
    (args: { componentId: string; input: OpsRequestFundInput }) =>
      requestOpsFund(caseId, args.componentId, args.input),
    'Fund request sent',
    'Could not ask for fund',
  );

export const useRequestOpsAmendment = (caseId: string) =>
  useItemMutation(
    (args: { componentId: string; input: OpsAmendmentInput }) =>
      requestOpsAmendment(caseId, args.componentId, args.input),
    'Sent to the seller for amendment',
    'Could not send for amendment',
  );

export const useOpsApprovals = (enabled: boolean) =>
  useQuery({
    queryKey: opsCasesQK.approvals,
    queryFn: listOpsApprovals,
    enabled,
    refetchInterval: 30_000,
  });

export const useDecideOpsApproval = () =>
  useItemMutation(
    (args: { caseId: string; componentId: string; input: OpsApprovalDecisionInput }) =>
      decideOpsApproval(args.caseId, args.componentId, args.input),
    'Decision saved',
    'Could not save the decision',
  );

export const useOpenOpsProof = () =>
  useMutation({
    mutationFn: (args: {
      caseId: string;
      componentId: string;
      /** One instalment's screenshot; omitted = the item's (last) one. */
      instalmentId?: string;
      viaAccounts?: boolean;
    }) =>
      args.viaAccounts
        ? fetchFundProof(args.caseId, args.componentId, args.instalmentId)
        : fetchOpsPaymentProof(args.caseId, args.componentId, args.instalmentId),
    onSuccess: (res) => {
      window.open(res.url, '_blank', 'noopener');
    },
    onError: (err) => toast.error(msg(err) ?? 'Could not open the payment proof'),
  });

export const useFundRequests = (view: 'requested' | 'paid') =>
  useQuery({
    queryKey: opsCasesQK.fundRequests(view),
    queryFn: () => listFundRequests(view),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

export const useMarkFundPaid = () =>
  useItemMutation(
    (args: { caseId: string; componentId: string; input: OpsMarkPaidInput }) =>
      markFundPaid(args.caseId, args.componentId, args.input),
    'Marked paid - operations is notified',
    'Could not mark paid',
  );

export const useOpsAmendments = (leadId?: string, enabled = true) =>
  useQuery({
    queryKey: opsCasesQK.amendments(leadId),
    queryFn: () => listOpsAmendments(leadId),
    enabled,
    refetchInterval: 60_000,
  });

export const useResolveOpsAmendment = () =>
  useItemMutation(
    (args: { caseId: string; componentId: string; input: OpsResolveAmendmentInput }) =>
      resolveOpsAmendment(args.caseId, args.componentId, args.input),
    'Resolved - back with operations',
    'Could not resolve',
  );

// --- Documents on the case ---

export const useOpsCaseDocuments = (caseId: string | null) =>
  useQuery({
    queryKey: opsCasesQK.documents(caseId ?? ''),
    queryFn: () => listOpsCaseDocuments(caseId as string),
    enabled: Boolean(caseId),
  });

export const useLeadOpsDocuments = (leadId: string, enabled = true) =>
  useQuery({
    queryKey: opsCasesQK.leadDocuments(leadId),
    queryFn: () => listLeadOpsDocuments(leadId),
    enabled,
  });

export const useUploadOpsCaseDocument = (caseId: string) =>
  useItemMutation(
    (input: OpsDocumentUploadInput) => uploadOpsCaseDocument(caseId, input),
    'Document uploaded',
    'Could not upload the document',
  );

export const useDeleteOpsCaseDocument = (caseId: string) =>
  useItemMutation(
    (docId: string) => deleteOpsCaseDocument(caseId, docId),
    'Document removed',
    'Could not remove the document',
  );

/** Opens the file in a new tab; `viaLead` uses the seller-side route. */
export const useOpsSellerScreenshots = (caseId: string) =>
  useQuery({
    queryKey: opsCasesQK.sellerScreenshots(caseId),
    queryFn: () => listOpsSellerScreenshots(caseId),
  });

export const useOpenOpsSellerScreenshot = () =>
  useMutation({
    mutationFn: (args: { caseId: string; screenshotId: string }) =>
      fetchOpsSellerScreenshotUrl(args.caseId, args.screenshotId),
    onSuccess: (res) => {
      window.open(res.url, '_blank', 'noopener');
    },
    onError: (err) => toast.error(msg(err) ?? 'Could not open the screenshot'),
  });

export const useOpenOpsDocument = () =>
  useMutation({
    mutationFn: (args: { caseId: string; docId: string; viaLead?: boolean }) =>
      args.viaLead
        ? fetchLeadOpsDocumentUrl(args.caseId, args.docId)
        : fetchOpsCaseDocumentUrl(args.caseId, args.docId),
    onSuccess: (res) => {
      window.open(res.url, '_blank', 'noopener');
    },
    onError: (err) => toast.error(msg(err) ?? 'Could not open the document'),
  });
